import React, { useEffect, useState } from 'react';
import { useNavigate, Link, useLocation } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { useAuth } from '../AuthContext';
import { toast } from 'sonner';
import { Loader2, Mail, Eye, EyeOff } from 'lucide-react';

// Map Supabase error messages to user-friendly messages
const getFriendlyError = (error) => {
  const msg = error?.message?.toLowerCase() || '';
  if (msg.includes('invalid api key')) return 'Service configuration error. Please contact support.';
  if (msg.includes('invalid login credentials')) return 'Incorrect email or password. Please try again.';
  if (msg.includes('email not confirmed')) return 'Please check your email and confirm your account before signing in.';
  if (msg.includes('too many requests') || msg.includes('rate limit')) return 'Too many attempts. Please wait a moment and try again.';
  if (msg.includes('user not found')) return 'No account found with this email. Please register first.';
  if (msg.includes('network') || msg.includes('fetch')) return 'Network error. Please check your internet connection.';
  if (msg.includes('invalid email')) return 'Please enter a valid email address.';
  if (msg.includes('signup is disabled')) return 'Registration is currently disabled. Please contact support.';
  return error?.message || 'Something went wrong. Please try again.';
};

const LoginPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { signInWithGoogle, signInWithEmail, resetPassword, isAuthenticated, loading } = useAuth();
  const [isLoggingIn, setIsLoggingIn] = useState(false);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [isResettingPassword, setIsResettingPassword] = useState(false);

  // Check for OAuth callback tokens in URL
  useEffect(() => {
    const hash = window.location.hash;
    if (hash && (hash.includes('access_token') || hash.includes('error'))) {
      console.log('OAuth callback detected on login page');
      // The AuthContext will handle this, just show loading
      setIsLoggingIn(true);
    }
  }, []);

  // Redirect if authenticated
  useEffect(() => {
    if (isAuthenticated && !loading) {
      console.log('User authenticated, redirecting to dashboard');
      // Get return URL from query params or default to dashboard
      const params = new URLSearchParams(location.search);
      const returnTo = params.get('returnTo') || '/dashboard';
      navigate(returnTo, { replace: true });
    }
  }, [isAuthenticated, loading, navigate, location]);

  const handleGoogleLogin = async () => {
    setIsLoggingIn(true);
    const { error } = await signInWithGoogle();
    
    if (error) {
      setIsLoggingIn(false);
      toast.error('Unable to sign in with Google', {
        description: getFriendlyError(error),
      });
    }
    // Success will redirect via Supabase OAuth flow
  };

  const handleEmailLogin = async (e) => {
    e.preventDefault();
    if (!email || !password) {
      toast.warning('Please enter both email and password.');
      return;
    }
    setIsLoggingIn(true);
    const { error } = await signInWithEmail(email, password);
    if (error) {
      setIsLoggingIn(false);
      toast.error('Login Failed', {
        description: getFriendlyError(error),
      });
    }
  };

  const handleForgotPassword = async () => {
    if (!email) {
      toast.warning('Please enter your email address first to reset your password.');
      return;
    }
    setIsResettingPassword(true);
    const { error } = await resetPassword(email);
    setIsResettingPassword(false);
    if (error) {
      toast.error('Password Reset Failed', {
        description: getFriendlyError(error),
      });
    } else {
      toast.success('Check Your Email', {
        description: 'A password reset link has been sent to your email.',
      });
    }
  };

  // Show loading if processing OAuth callback or checking auth
  if (loading || isLoggingIn) {
    return (
      <div
        className="min-h-screen flex items-center justify-center px-4"
        style={{ backgroundColor: 'var(--bg-primary)', paddingTop: '80px' }}
      >
        <div className="text-center">
          <Loader2 className="animate-spin mx-auto mb-4" size={40} style={{ color: 'var(--olive-primary)' }} />
          <p className="body-regular" style={{ color: 'var(--text-secondary)' }}>
            {isLoggingIn ? 'Signing you in...' : 'Checking authentication...'}
          </p>
        </div>
      </div>
    );
  }

  return (
    <div
      className="min-h-screen flex items-center justify-center px-4"
      style={{ backgroundColor: 'var(--bg-primary)', paddingTop: '80px' }}
      data-testid="login-page"
    >
      <div
        className="w-full max-w-md p-8"
        style={{
          backgroundColor: 'var(--bg-secondary)',
          border: '1px solid var(--border-light)'
        }}
      >
        <div className="text-center mb-8">
          <h1 className="heading-1 mb-2">Welcome Back</h1>
          <p className="body-regular" style={{ color: 'var(--text-secondary)' }}>
            Sign in to your account
          </p>
        </div>

        <div className="space-y-4">
          {/* Email/Password Sign In */}
          <form onSubmit={handleEmailLogin} className="space-y-4">
            <div>
              <Label htmlFor="email">Email Address</Label>
              <div className="relative">
                <Input
                  id="email"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="you@example.com"
                  required
                  style={{ borderRadius: 0, paddingRight: '2.5rem' }}
                  data-testid="email-input"
                />
                <Mail className="absolute right-3 top-1/2 -translate-y-1/2" size={16} style={{ color: 'var(--text-light)' }} />
              </div>
            </div>

            <div>
              <Label htmlFor="password">Password</Label>
              <div className="relative">
                <Input
                  id="password"
                  type={showPassword ? 'text' : 'password'}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="Enter your password"
                  required
                  style={{ borderRadius: 0, paddingRight: '2.5rem' }}
                  data-testid="password-input"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2"
                  style={{ color: 'var(--text-light)', background: 'none', border: 'none', cursor: 'pointer', padding: 0 }}
                  tabIndex={-1}
                >
                  {showPassword ? <EyeOff size={16} /> : <Eye size={16} />}
                </button>
              </div>
            </div>

            <div className="flex justify-end">
              <button
                type="button"
                onClick={handleForgotPassword}
                disabled={isResettingPassword}
                className="body-small"
                style={{
                  color: 'var(--interactive-base)',
                  background: 'none',
                  border: 'none',
                  cursor: 'pointer',
                  textDecoration: 'underline',
                  padding: 0
                }}
              >
                {isResettingPassword ? 'Sending...' : 'Forgot password?'}
              </button>
            </div>

            <Button type="submit" className="btn-primary w-full" data-testid="email-login-btn">
              Sign in with Email
            </Button>
          </form>

          {/* Divider */}
          <div className="relative my-6">
            <div className="absolute inset-0 flex items-center">
              <div className="w-full border-t" style={{ borderColor: 'var(--border-light)' }}></div>
            </div>
            <div className="relative flex justify-center">
              <span className="px-4 body-small" style={{ backgroundColor: 'var(--bg-secondary)', color: 'var(--text-light)' }}>
                or continue with
              </span>
            </div>
          </div>

          {/* Google Sign In */}
          <button
            onClick={handleGoogleLogin}
            className="w-full flex items-center justify-center gap-3 px-6 py-3 border transition-all hover:bg-[var(--bg-subtle)]"
            style={{
              borderColor: 'var(--border-medium)',
              backgroundColor: 'var(--bg-primary)',
              color: 'var(--text-primary)',
              fontFamily: "'Inter', sans-serif",
              cursor: 'pointer'
            }}
            data-testid="google-login-btn"
          >
            <svg width="20" height="20" viewBox="0 0 24 24">
              <path
                fill="#4285F4"
                d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
              />
              <path
                fill="#34A853"
                d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
              />
              <path
                fill="#FBBC05"
                d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
              />
              <path
                fill="#EA4335"
                d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
              />
            </svg>
            Sign in with Google
          </button>
        </div>

        <div className="mt-8 text-center">
          <p className="body-small" style={{ color: 'var(--text-secondary)' }}>
            Don't have an account?{' '}
            <Link
              to="/register"
              style={{ color: 'var(--interactive-base)', textDecoration: 'underline' }}
            >
              Create one
            </Link>
          </p>
        </div>

        <div className="mt-6 pt-6 border-t" style={{ borderColor: 'var(--border-light)' }}>
          <p className="text-meta text-center" style={{ color: 'var(--text-light)' }}>
            By signing in, you agree to our Terms of Service and Privacy Policy
          </p>
        </div>
      </div>
    </div>
  );
};

export default LoginPage;
